import React from 'react';
import { Formatting } from '../utils';
import styled from 'styled-components';
import moment from 'moment';
import {
	FinancesOrderView,
	getReadablePaymentType,
	getReadablePriceType,
	OrderType,
	PaymentType,
	PriceType,
	getReadableBdo,
} from 'types';

import { cyan, gold, green, magenta, purple, red, volcano } from '@ant-design/colors';

const Print = styled.div``;

const PrintTable = styled.table`
	width: 100%;
	page-break-inside: auto;

	tr {
		page-break-inside: avoid;
		page-break-after: auto;

		&:nth-child(2n) {
			background: #eee;
		}

		> td,
		> th {
			padding: 4px 8px;
			text-align: center;
		}

		> th {
			font-size: 14px;

			> div {
				display: flex;
				flex-direction: column;

				> h5 {
					font-weight: 600;
					margin-bottom: 4px;
				}

				> span {
					font-weight: 400;
					font-size: 8px;
				}
			}
		}

		> td {
			font-size: 10px;

			> div {
				display: flex;
				flex-direction: column;

				> h6 {
					font-weight: 600;
					font-size: 12px;
					margin-bottom: 2px;
				}
			}
		}
	}
`;

const PrintTitle = styled.h2`
	width: 100%;
	margin-top: 16px;
	margin-bottom: 8px;
	text-align: center;
	font-size: 18px;
`;

const PrintSubtitle = styled.h3`
	width: 100%;
	margin-top: 16px;
	margin-bottom: 32px;
	text-align: center;
	font-size: 14px;
`;

const DayTitle = styled(PrintSubtitle)`
	margin-top: 48px;
`;

interface SummaryProps {
	first?: boolean;
}

const Summary = styled.h4<SummaryProps>`
	text-align: center;
	margin: ${({ first }) => (first ? '24px' : '0')} 0 0 0;
	font-weight: 400;
`;

const SummaryTitle = styled.h3`
	text-align: center;
	margin: 32px 0 0 0;
	font-weight: 500;
`;

const PageBreak = styled.div`
	&:not(:last-of-type) {
		page-break-after: always;
	}
`;

const getColor = (order: FinancesOrderView, type: 'delivery' | 'replacement' | 'claiming') => {
	if (order.type === OrderType.classic) {
		if (order.onPlace) {
			return purple[5];
		}

		switch (type) {
			case 'delivery':
				return green[5];

			case 'replacement':
				return gold[5];

			case 'claiming':
				return red[5];
		}
	}

	if (order.type === OrderType.loose) {
		if (order.onPlace) {
			return magenta[3];
		}

		switch (type) {
			case 'delivery':
				return cyan[4];

			case 'replacement':
				return magenta[3];

			case 'claiming':
				return magenta[3];
		}
	}

	return volcano[5];
};

const getReadableType = (order: FinancesOrderView, type: 'delivery' | 'replacement' | 'claiming') => {
	if (order.type === OrderType.classic) {
		if (order.onPlace) {
			return 'Załadunek';
		}

		switch (type) {
			case 'delivery':
				return 'Podstawienie';

			case 'replacement':
				return 'Wymiana';

			case 'claiming':
				return 'Odbiór';
		}
	}

	if (order.type === OrderType.loose) {
		if (order.onPlace) {
			return 'Załadunek';
		}

		switch (type) {
			case 'delivery':
				return 'Podstawienie';

			case 'claiming':
				return 'Odbiór';
		}
	}

	return 'Nieznany typ';
};

interface Props {
	orders: FinancesOrderView[] | undefined;
	from: moment.Moment;
	to: moment.Moment;
}

const FinancesPrint: React.FC<Props> = ({ from, to, orders }) => {
	if (!orders) {
		return <div></div>;
	}

	const deliveriesByDay = orders.reduce<Record<string, FinancesOrderView[]>>((acc, order) => {
		const dateString = moment(order.deliveryDate).format('DD.MM.YYYY');
		if (!moment(order.deliveryDate).isBetween(from, to)) {
			return acc;
		}

		const entry = acc[dateString] ?? [];
		entry.push(order);

		return {
			...acc,
			[dateString]: entry,
		};
	}, {});
	const claimingsByDay = orders.reduce<Record<string, FinancesOrderView[]>>((acc, order) => {
		if (!order.claimingDate) {
			return acc;
		}

		if (order.onPlace) {
			return acc;
		}

		if (order.relatedId && order.relatedId > order.id) {
			// Skip orders that were replaced by another order
			return acc;
		}

		if (!moment(order.claimingDate).isBetween(from, to)) {
			return acc;
		}

		const dateString = moment(order.claimingDate).format('DD.MM.YYYY');

		const entry = acc[dateString] ?? [];
		entry.push(order);

		return {
			...acc,
			[dateString]: entry,
		};
	}, {});
	const allDays = new Set([...Object.keys(deliveriesByDay), ...Object.keys(claimingsByDay)]);
	const ordersByDay = Object.fromEntries(
		Array.from(allDays).map((day) => {
			const deliveries = deliveriesByDay[day] ?? [];
			const claimings = claimingsByDay[day] ?? [];

			return [
				day,
				{
					deliveries: deliveries,
					claimings: claimings,
				},
			];
		})
	);

	const totalNetIncome = orders.reduce<number>((acc, order) => acc + order.netPrice, 0);
	const totalNetCashIncome = orders.reduce<number>(
		(acc, order) => (order.wantedPaymentType === PaymentType.cash ? acc + order.netPrice : acc),
		0
	);
	const totalGrossIncome = orders.reduce<number>((acc, order) => acc + order.grossPrice, 0);
	const totalGrossCashIncome = orders.reduce<number>(
		(acc, order) => (order.wantedPaymentType === PaymentType.cash ? acc + order.grossPrice : acc),
		0
	);

	return (
		<Print className="print">
			<PrintTitle>
				Wydruk zamówień między {Formatting.epochToDate(from, true)} a {Formatting.epochToDate(to, true)}
			</PrintTitle>
			<PrintSubtitle>Stan z dnia {Formatting.epochToDate(Date.now())}</PrintSubtitle>
			{Object.entries(ordersByDay).map(([day, orders]) => {
				// const netIncome = orders.reduce<number>((acc, order) => acc + order.netPrice, 0);
				// const netCashIncome = orders.reduce<number>(
				// 	(acc, order) => (order.wantedPaymentType === PaymentType.cash ? acc + order.netPrice : acc),
				// 	0
				// );
				// const grossIncome = orders.reduce<number>((acc, order) => acc + order.grossPrice, 0);
				// const grossCashIncome = orders.reduce<number>(
				// 	(acc, order) => (order.wantedPaymentType === PaymentType.cash ? acc + order.grossPrice : acc),
				// 	0
				// );

				return (
					<React.Fragment key={day}>
						<DayTitle>Podstawienia {day}:</DayTitle>
						<PrintTable>
							<tbody>
								<tr>
									<th>Rodzaj</th>
									<th>
										<div>
											<h5>Klient</h5>
											<span>Nazwa</span>
											<span>NIP</span>
											<span>Email</span>
										</div>
									</th>
									<th>
										<div>
											<h5>Cena</h5>
											<span>Brutto / Netto</span>
											<span>Typ płatności</span>
										</div>
									</th>
									<th>
										<div>
											<h5>Adres</h5>
											<span>BDO</span>
											<span>Rodzaj odpadów</span>
										</div>
									</th>
									<th>Faktura</th>
									<th>Data</th>
								</tr>
								{orders.deliveries.map((order, i) => (
									<tr key={`delivery-${order.id}-${order.type}`}>
										<td
											style={{
												background: getColor(order, order.wasReplacement ? 'replacement' : 'delivery'),
												minWidth: '100px',
											}}
										>
											<div>
												<h6>{i + 1}</h6>
												<h6>{order.type == OrderType.classic ? 'Kontener' : 'Big Bag'}</h6>
												<h6>{getReadableType(order, order.wasReplacement ? 'replacement' : 'delivery')}</h6>
											</div>
										</td>
										<td>
											<div>
												<span>{order.client.name}</span>
												<span>{order.client.nip}</span>
												<span>{order.client.email}</span>
											</div>
										</td>
										<td>
											<div>
												<h6>
													{Formatting.money(order.priceType === PriceType.net ? order.netPrice : order.grossPrice)}
												</h6>
												<span>{getReadablePriceType(order.priceType)}</span>
												<span>{getReadablePaymentType(order.wantedPaymentType)}</span>
												<span>{order.grossPrice > order.paid ? 'Nieopłacone' : 'Opłacone'}</span>
											</div>
										</td>
										<td>
											<div>
												<h6>{order.address}</h6>
												<span>{getReadableBdo(order.bdo)}</span>
												<span>{order.wasteType}</span>
											</div>
										</td>
										<td>{order.invoice ?? '-'}</td>
										<td>{Formatting.epochToDate(order.deliveryDate, true)}</td>
									</tr>
								))}
							</tbody>
						</PrintTable>
						<DayTitle>Odbiory {day}:</DayTitle>
						<PrintTable>
							<tbody>
								<tr>
									<th>Rodzaj</th>
									<th>
										<div>
											<h5>Klient</h5>
											<span>Nazwa</span>
											<span>NIP</span>
											<span>Email</span>
										</div>
									</th>
									<th>
										<div>
											<h5>Cena</h5>
											<span>Brutto / Netto</span>
											<span>Typ płatności</span>
										</div>
									</th>
									<th>
										<div>
											<h5>Adres</h5>
											<span>BDO</span>
											<span>Rodzaj odpadów</span>
										</div>
									</th>
									<th>Faktura</th>
									<th>Data</th>
								</tr>
								{orders.claimings.map((order, i) => (
									<tr key={`claiming-${order.id}-${order.type}`}>
										<td style={{ background: getColor(order, 'claiming'), minWidth: '100px' }}>
											<div>
												<h6>{i + 1}</h6>
												<h6>{order.type == OrderType.classic ? 'Kontener' : 'Big Bag'}</h6>
												<h6>{getReadableType(order, 'claiming')}</h6>
											</div>
										</td>
										<td>
											<div>
												<span>{order.client.name}</span>
												<span>{order.client.nip}</span>
												<span>{order.client.email}</span>
											</div>
										</td>
										<td>
											<div>
												<h6>
													{Formatting.money(order.priceType === PriceType.net ? order.netPrice : order.grossPrice)}
												</h6>
												<span>{getReadablePriceType(order.priceType)}</span>
												<span>{getReadablePaymentType(order.wantedPaymentType)}</span>
												<span>{order.grossPrice > order.paid ? 'Nieopłacone' : 'Opłacone'}</span>
											</div>
										</td>
										<td>
											<div>
												<h6>{order.address}</h6>
												<span>{getReadableBdo(order.bdo)}</span>
												<span>{order.wasteType}</span>
											</div>
										</td>
										<td>{order.invoice ?? '-'}</td>
										<td>{Formatting.epochToDate(order.claimingDate, true)}</td>
									</tr>
								))}
							</tbody>
						</PrintTable>
						{/* <Summary first>
							Przychód brutto: {Formatting.money(grossIncome)} ({Formatting.money(grossCashIncome)} gotówka)
							<br />
							Przychód netto: {Formatting.money(netIncome)} ({Formatting.money(netCashIncome)} gotówka)
						</Summary> */}
						<PageBreak />
					</React.Fragment>
				);
			})}
			<SummaryTitle>Sumarycznie:</SummaryTitle>
			<Summary>
				Przychód brutto: {Formatting.money(totalGrossIncome)} ({Formatting.money(totalGrossCashIncome)} gotówka)
				<br />
				Przychód netto: {Formatting.money(totalNetIncome)} ({Formatting.money(totalNetCashIncome)} gotówka)
			</Summary>
		</Print>
	);
};

export default class extends React.PureComponent<Props> {
	override render() {
		return <FinancesPrint {...this.props} />;
	}
}
